import pino, {Bindings, ChildLoggerOptions} from 'pino'

/*
 * Pino is a structured logger that works in both browser and server environments.
 *
 * @example Basic logging with metadata:
 *
 * logger.info('User performed search')
 * logger.info('User performed search', { userId: 123, action: 'search' })
 * logger.warn('Slow response detected', { path: '/api/data' })
 *
 * @example With error object:
 * logger.error('Failed to fetch data', new Error('Network failure'))
 *
 * @example The error can be passed inside the metadata object with the key "err".
 * logger.error('Failed to fetch data', { err: new Error('Network failure'), userId: 123})
 *
 * @note When passing the error inside the metadata object it must be with the key "err", otherwise it will not be logged as an error.
 *
 * @see https://github.com/pinojs/pino/blob/main/docs/api.md
 */

interface LogFn {
  (msg: string, obj?: object | Error | unknown): void
}

export interface Logger {
  debug: LogFn
  info: LogFn
  warn: LogFn
  error: LogFn
  child: (bindings: Bindings, options?: ChildLoggerOptions) => Logger
}

// Pino pretty makes the logs more readable in the console for local development
const transport =
  typeof process !== 'undefined' &&
  process.env?.NEXT_PUBLIC_ENV?.includes('dev_')
    ? {
        target: 'pino-pretty',
        options: {
          colorize: true
        }
      }
    : undefined

const logger = pino({
  base: null,
  level: 'info',
  timestamp: pino.stdTimeFunctions.isoTime,
  transport,
  formatters: {
    level: label => ({level: label})
  },
  hooks: {
    // Using this hook the parameters can be flipped from logger.info(obj, msg) to logger.info(msg, obj) as it's ergonomically better.
    // This is only for the server side logging.
    // @see https://getpino.io/#/docs/api?id=logmethod
    logMethod(inputArgs, method) {
      if (inputArgs.length >= 2) {
        const arg1 = inputArgs.shift() as string
        const arg2 = inputArgs.shift()
        return method.apply(this, [arg2, arg1, ...inputArgs])
      }
      return method.apply(this, inputArgs)
    }
  }
}) as Logger

export {logger}
